export type PracticeArea = {
  slug: string;
  name: string;
  shortName: string;
  headline: string;
  intro: string;
  primaryKeyword: string;
  searchVolume: number;
  topKeywords: string[];
  painPoints: string[];
  deliverables: string[];
  results: { metric: string; label: string }[];
  faqs: { q: string; a: string }[];
};

export const practiceAreas: PracticeArea[] = [
  {
    slug: "personal-injury-lawyer-seo",
    name: "Personal Injury Lawyer SEO",
    shortName: "Personal Injury",
    headline: "Rank #1 for the cases that actually pay.",
    intro: "Personal injury is the most competitive vertical in legal SEO. We've helped PI firms outrank decades-old competitors for million-dollar keywords like 'personal injury lawyer near me' and 'car accident attorney [city]'.",
    primaryKeyword: "personal injury lawyer seo",
    searchVolume: 10570,
    topKeywords: ["personal injury lawyer seo", "seo for personal injury lawyer", "personal injury law firm seo", "personal injury law seo", "personal injury lawyer seo services", "car accident lawyer seo", "best seo for personal injury lawyers"],
    painPoints: ["Spending $80–$200 per click on Google Ads with poor case quality", "Outranked by national 'lawyer-matching' lead resellers", "Generic agencies that don't understand contingency-fee economics", "Slow site speed killing Local Pack visibility"],
    deliverables: ["City + practice landing pages (e.g. /car-accident-lawyer-houston)", "Google Business Profile optimization for every office", "Backlinks from authoritative legal & news publishers", "Content built around case-type intent: motorcycle, truck, slip & fall, wrongful death", "Schema markup: LegalService, Attorney, FAQPage, Review", "Site speed & Core Web Vitals tuned for mobile-first ranking"],
    results: [
      { metric: "+312%", label: "Organic case inquiries in 9 months" },
      { metric: "Top 3", label: "For 'personal injury lawyer [city]' in 4 markets" },
      { metric: "$2.8M", label: "In recovered fees attributed to organic in 12 months" },
    ],
    faqs: [
      { q: "How long until a personal injury firm ranks on page one?", a: "For competitive metros, expect 6–9 months for primary city + practice terms, with long-tail and suburb terms ranking in 3–4 months. We publish a 30-60-90 day plan in every engagement." },
      { q: "How do you report on search performance?", a: "No ethical agency does, Google forbids it. We guarantee a documented strategy, monthly deliverables, transparent reporting, and the work itself." },
      { q: "Can you work alongside our existing intake or PPC team?", a: "Yes. We regularly collaborate with in-house marketing directors, intake CRMs (Lawmatics, Litify, Filevine), and PPC agencies." },
    ],
  },
  {
    slug: "dui-dwi-lawyer-seo",
    name: "DUI / DWI Lawyer SEO",
    shortName: "DUI / DWI",
    headline: "Get found at 2 AM when the call matters most.",
    intro: "DUI clients search at the worst moment of their life, and they hire fast. We build DUI defense SEO that wins the local pack, the map, and the 'arrested last night' moment of truth.",
    primaryKeyword: "dui lawyer seo",
    searchVolume: 960,
    topKeywords: ["dui lawyer seo", "dui attorney seo", "dwi lawyer seo", "seo for dui attorneys", "criminal defense dui seo"],
    painPoints: ["Massive Google Ads spend with low conversion at 2 AM", "Bail bond and lead-gen sites stealing first-page real estate", "Reputation issues (mugshot sites) outranking your firm name", "City + 'first offense' / 'felony' modifiers untargeted"],
    deliverables: ["Hyper-local city pages: /dui-lawyer-[city]", "Modifier pages: first-offense, felony, refusal, CDL, underage", "Mobile-first design with click-to-call above the fold", "Same-night intake schema + EmergencyService markup", "Brand-protection content to bury negative SERP results", "Reviews engine: Google, Avvo, Martindale"],
    results: [
      { metric: "+417%", label: "After-hours mobile calls within 6 months" },
      { metric: "Position 1", label: "'DUI lawyer [city]' in 3 of 4 target markets" },
      { metric: "82%", label: "Of new leads attributed to organic search" },
    ],
    faqs: [
      { q: "Will an SEO strategy outperform our Google Ads spend?", a: "Over 12+ months almost always yes, DUI CPCs of $40–$120 make organic cost-per-acquisition 5–10x cheaper. We typically recommend running both, with SEO as the long-term moat." },
      { q: "How do you handle negative search results?", a: "We build authority assets (bio pages, articles, schema) to outrank mugshot and complaint sites for your firm name and attorney names." },
    ],
  },
  {
    slug: "family-law-lawyer-seo",
    name: "Family Law & Divorce SEO",
    shortName: "Family Law",
    headline: "Be the firm people trust with the hardest decision of their life.",
    intro: "Divorce, custody, and family law searchers spend weeks researching before they call. We build the content, authority, and local presence that earns that call.",
    primaryKeyword: "divorce lawyer seo",
    searchVolume: 9030,
    topKeywords: ["divorce lawyer seo", "family law seo", "divorce attorney seo", "seo for family law", "child custody lawyer seo"],
    painPoints: ["High research-phase traffic that never converts", "Competing with national directories (FindLaw, Justia)", "Sensitive topics need EEAT-grade content", "Multi-county practice areas hard to rank simultaneously"],
    deliverables: ["Topic clusters: divorce, custody, support, prenup, mediation, collaborative", "County + practice pages for every venue you litigate in", "Attorney bio pages with proper Schema.org Person markup", "Long-form pillar content (3,000+ words) on high-intent queries", "Testimonial & case-result content (ethically anonymized)", "Lead magnets: divorce checklist, custody calendar"],
    results: [
      { metric: "+248%", label: "Organic consultations in 12 months" },
      { metric: "37", label: "Page-1 rankings for divorce + city keywords" },
      { metric: "4.2x", label: "ROI vs. paid search benchmarks" },
    ],
    faqs: [
      { q: "Family law touches sensitive topics, how do you handle EEAT?", a: "We treat family law as YMYL (Your Money or Your Life) content. Every page is reviewed by a licensed attorney, includes author bios with credentials, citations to statutes, and proper Article + Person schema." },
      { q: "Can you target multiple counties without diluting authority?", a: "Yes, through a hub-and-spoke architecture and tight internal linking, plus separate Google Business Profile optimization for each office." },
    ],
  },
  {
    slug: "criminal-defense-lawyer-seo",
    name: "Criminal Defense Lawyer SEO",
    shortName: "Criminal Defense",
    headline: "Defendants Google their charge before they call a lawyer.",
    intro: "Criminal defense SEO is about being the answer to specific, fearful queries, felony, misdemeanor, federal, drug, weapons, assault. We map content to every charge in your jurisdiction.",
    primaryKeyword: "criminal defense lawyer seo",
    searchVolume: 3780,
    topKeywords: ["criminal law seo", "criminal defense attorney seo", "criminal defense lawyer seo", "criminal defense law firm seo", "criminal law seo services"],
    painPoints: ["Hundreds of charge types, content burden is huge", "Federal vs state jurisdiction confusion in search intent", "Lead-gen monsters (Nolo, Avvo, LegalMatch) dominate", "Reputation defense for attorneys with prior coverage"],
    deliverables: ["Charge-specific pages for every offense in your state", "Federal practice pages (white collar, drug trafficking, weapons)", "Verdict & dismissal case-result library", "Local sponsorships & PR for high-DR backlinks", "Court-specific landing pages for each county", "Bilingual content where market warrants"],
    results: [
      { metric: "+196%", label: "Qualified consultations year-over-year" },
      { metric: "#1", label: "Local pack for 11 charge + city combos" },
      { metric: "DR 58", label: "Domain authority growth in 14 months" },
    ],
    faqs: [
      { q: "How many charge pages do we actually need?", a: "Most firms benefit from 25–60 charge-specific pages, enough to cover real volume without diluting authority. We rank them by your state's actual case data." },
    ],
  },
  {
    slug: "estate-planning-lawyer-seo",
    name: "Estate Planning & Probate SEO",
    shortName: "Estate Planning",
    headline: "Win the wills, trusts, and probate searches in your market.",
    intro: "Estate planning and probate clients are high-intent and high-LTV. We rank firms for trust formation, probate administration, elder law, and estate litigation queries.",
    primaryKeyword: "seo for estate planning attorneys",
    searchVolume: 2220,
    topKeywords: ["elder law seo", "seo for estate planning attorneys", "estate planning lawyer seo", "probate lawyer seo", "trust attorney seo"],
    painPoints: ["Audience skews older, site usability & speed critical", "DIY tools (LegalZoom) intercepting top-funnel traffic", "Long sales cycle, needs nurture content", "Trust topics demand maximum credibility signals"],
    deliverables: ["Content matrix: wills, revocable trusts, irrevocable trusts, probate, elder law, Medicaid planning", "City + practice pages for every market", "Senior-friendly UX: large type, simple nav, accessible color contrast", "Free will checklist & probate timeline lead magnets", "Webinar funnel + email nurture content"],
    results: [
      { metric: "+184%", label: "Qualified estate-planning consults" },
      { metric: "3.1x", label: "Increase in trust-formation inquiries" },
      { metric: "+62%", label: "Engagement vs. industry average" },
    ],
    faqs: [
      { q: "How do we compete with LegalZoom on broad terms?", a: "We don't. We target the underserved local + complexity keywords ('irrevocable trust attorney [city]', 'probate lawyer [county]') where LegalZoom can't compete on expertise." },
    ],
  },
  {
    slug: "immigration-lawyer-seo",
    name: "Immigration Lawyer SEO",
    shortName: "Immigration",
    headline: "Rank for visa, green card, and deportation defense queries.",
    intro: "Immigration is a global, multilingual, high-volume practice. We build SEO programs that rank for visa categories, country-of-origin queries, and bilingual local search.",
    primaryKeyword: "immigration attorney seo",
    searchVolume: 1550,
    topKeywords: ["immigration attorney seo", "seo for immigration attorneys", "immigration law seo", "immigration lawyer seo", "immigration law firm seo"],
    painPoints: ["Bilingual content needs (Spanish, Mandarin, Arabic, etc.)", "Policy changes invalidate content rapidly", "Federal regulatory complexity, credibility is everything", "Competing with USCIS itself for informational queries"],
    deliverables: ["Bilingual / hreflang content architecture", "Visa-category page library: H-1B, EB-5, family-based, asylum, DACA", "Country-of-origin landing pages", "Regulatory news feed + content refresh schedule", "Attorney credentials + AILA membership markup"],
    results: [
      { metric: "+221%", label: "Organic consultations" },
      { metric: "47", label: "Page-1 rankings across 2 languages" },
      { metric: "5 states", label: "Multi-jurisdictional ranking footprint" },
    ],
    faqs: [
      { q: "Do you handle Spanish-language SEO?", a: "Yes, native-speaker writers, proper hreflang, and Spanish keyword research. We treat it as a first-class market, not a translation afterthought." },
    ],
  },
  {
    slug: "bankruptcy-lawyer-seo",
    name: "Bankruptcy Lawyer SEO",
    shortName: "Bankruptcy",
    headline: "Capture Chapter 7, 13, and business bankruptcy intent.",
    intro: "Bankruptcy searchers are in financial distress and ready to act. We rank firms for Chapter 7, Chapter 13, debt relief, and small-business bankruptcy in their local market.",
    primaryKeyword: "bankruptcy lawyer seo",
    searchVolume: 1280,
    topKeywords: ["seo for bankruptcy law firms", "bankruptcy lawyer seo", "bankruptcy law seo", "las vegas bankruptcy attorney seo", "chapter 7 lawyer seo"],
    painPoints: ["Free consultation ad copy wars eating budgets", "Credit repair scam sites polluting SERPs", "Local pack dominated by 1–2 established competitors", "High call-to-conversion ratio requires excellent intake"],
    deliverables: ["Chapter 7 vs Chapter 13 comparison content", "Means test calculator & qualification quiz", "Local pack optimization for 'bankruptcy lawyer near me'", "Debt-relief content cluster", "Free consultation landing pages with form + click-to-call"],
    results: [
      { metric: "+267%", label: "Qualified bankruptcy consults" },
      { metric: "Top 3", label: "Local pack in primary metro" },
    ],
    faqs: [
      { q: "What ranks fastest in bankruptcy SEO?", a: "Local pack and chapter-specific city pages, typically inside 4 months. Broader debt-relief informational content takes 6–9 months to build authority." },
    ],
  },
  {
    slug: "employment-law-lawyer-seo",
    name: "Employment Lawyer SEO",
    shortName: "Employment Law",
    headline: "Win discrimination, wage-and-hour, and wrongful termination cases.",
    intro: "Employment law SEO is split between plaintiff-side (employee) and defense-side (employer), and they need entirely different content strategies. We build both.",
    primaryKeyword: "employment lawyer seo",
    searchVolume: 570,
    topKeywords: ["employment lawyer seo", "employment law firm seo", "employment law seo", "seo for employment law attorneys"],
    painPoints: ["Same keywords serve opposite client types", "Long research phase before retention", "Federal vs state law jurisdiction complexity"],
    deliverables: ["Separate plaintiff and defense content hubs", "Practice-specific pages: discrimination, harassment, retaliation, FLSA, FMLA, NLRA", "State + federal law content for every jurisdiction", "Free case-evaluation funnel"],
    results: [
      { metric: "+158%", label: "Plaintiff intake calls" },
      { metric: "+91%", label: "Employer-side RFPs from organic" },
    ],
    faqs: [
      { q: "Can you serve both plaintiff and defense firms?", a: "Not in the same market, it's a conflict. We serve one side per geography." },
    ],
  },
  {
    slug: "workers-compensation-lawyer-seo",
    name: "Workers' Compensation Lawyer SEO",
    shortName: "Workers' Comp",
    headline: "Get hurt workers calling your firm first.",
    intro: "Workers' comp is purely local, purely intent-driven, and dominated by who shows up in the local pack. We make that you.",
    primaryKeyword: "workers comp lawyer seo",
    searchVolume: 260,
    topKeywords: ["workers comp lawyer seo", "workers compensation attorney seo", "injured worker lawyer seo"],
    painPoints: ["Local pack is everything", "Industry-specific injuries need dedicated content", "Insurance companies pushing their own preferred-counsel programs"],
    deliverables: ["Industry-injury content: construction, healthcare, warehouse, trucking", "City + practice landing pages", "Spanish-language content where market demands", "Aggressive Google Business Profile optimization"],
    results: [
      { metric: "+203%", label: "Organic intake calls" },
      { metric: "#1", label: "Local pack in target city" },
    ],
    faqs: [
      { q: "Is workers' comp SEO different from PI SEO?", a: "Yes, different intent, different statutory framework, different competitor set. We don't recycle PI content for workers' comp." },
    ],
  },
  {
    slug: "tax-law-lawyer-seo",
    name: "Tax Lawyer SEO",
    shortName: "Tax Law",
    headline: "Rank for IRS, audit defense, and tax controversy queries.",
    intro: "Tax law clients are scared, sophisticated, or both. We build SEO that ranks for IRS audit defense, tax debt relief, offshore disclosure, and high-net-worth planning.",
    primaryKeyword: "tax attorney seo",
    searchVolume: 250,
    topKeywords: ["tax law seo company", "tax attorney seo", "tax law firm seo", "tax lawyer seo"],
    painPoints: ["Tax-resolution scam sites flood the SERP", "Complex law requires expert-level content", "Seasonal volatility around April"],
    deliverables: ["Trust signals: CPA + JD credentials, bar admissions, IRS representation", "Topic clusters: audit, offers in compromise, installment agreements, FBAR", "Trust & estate tax planning content for HNW clients", "Year-round content calendar (not just tax season)"],
    results: [
      { metric: "+148%", label: "Qualified tax controversy leads" },
    ],
    faqs: [
      { q: "How do you separate us from tax-resolution scam companies?", a: "We lean on your credentials hard, bar admission, EA/CPA dual qualifications, courtroom history, and proper Schema.org Attorney markup." },
    ],
  },
  {
    slug: "medical-malpractice-lawyer-seo",
    name: "Medical Malpractice Lawyer SEO",
    shortName: "Medical Malpractice",
    headline: "Rank for the high-value cases other firms can't touch.",
    intro: "Medical malpractice is a small-volume, massive-value vertical. We rank firms for birth injury, surgical error, misdiagnosis, and nursing home neglect.",
    primaryKeyword: "seo for medical malpractice attorneys",
    searchVolume: 70,
    topKeywords: ["medical malpractice attorney seo", "seo for medical malpractice attorneys", "birth injury lawyer seo"],
    painPoints: ["Low volume, every search matters", "Statute of limitations urgency in copy", "Expert-witness credibility threshold"],
    deliverables: ["Sub-practice pages: birth injury, surgical, misdiagnosis, nursing home", "Case-result library (de-identified per HIPAA)", "Expert medical reviewer co-author credits", "Premium PR placements for high-DR backlinks"],
    results: [
      { metric: "+131%", label: "Qualified high-value case inquiries" },
    ],
    faqs: [
      { q: "Is the keyword volume worth investing in SEO?", a: "Yes, average MedMal case value is $200K–$2M+. A single converted case typically returns 10x annual SEO spend." },
    ],
  },
  {
    slug: "real-estate-law-lawyer-seo",
    name: "Real Estate Lawyer SEO",
    shortName: "Real Estate Law",
    headline: "Be the closing attorney every agent recommends.",
    intro: "Real estate law SEO is part B2C (buyers, sellers, landlords, tenants) and part B2B (agents, brokers, lenders, investors). We build content for both.",
    primaryKeyword: "real estate law seo",
    searchVolume: 260,
    topKeywords: ["real estate law seo", "real estate law seo company", "title attorney seo", "closing lawyer seo"],
    painPoints: ["Title companies competing for closing keywords", "Investor & landlord content very different from consumer", "Local relationship-driven referrals dominate"],
    deliverables: ["Closing & title insurance content", "Landlord-tenant practice pages", "Investor-focused content (1031, syndications, LLCs)", "Agent/broker partnership co-marketing content"],
    results: [{ metric: "+112%", label: "Closing volume from organic referrals" }],
    faqs: [{ q: "Can SEO replace agent referrals?", a: "No, it complements them. We typically see 30–50% of organic leads coming through agent-recommended search ('best real estate attorney [city]')." }],
  },
  {
    slug: "corporate-business-lawyer-seo",
    name: "Corporate & Business Lawyer SEO",
    shortName: "Business Law",
    headline: "Get found by founders, executives, and in-house counsel.",
    intro: "Business law SEO targets sophisticated buyers, founders, CFOs, GCs. We build authority content that gets you in the consideration set for high-value transactional and litigation work.",
    primaryKeyword: "business lawyer seo",
    searchVolume: 1210,
    topKeywords: ["seo london corporate law", "business lawyer seo", "business law seo", "corporate attorney seo", "startup lawyer seo"],
    painPoints: ["Sophisticated buyers consume long-form content", "RFPs come from referrals, SEO supports, doesn't replace", "Sector specialization (SaaS, fintech, life sciences) needs dedicated content"],
    deliverables: ["Sector-vertical content (SaaS, life sciences, real estate, etc.)", "Long-form guides: formation, M&A, contracts, governance, financing", "Attorney bios with deal sheets & Super Lawyers / Chambers credentials", "Thought leadership PR for tier-1 backlinks"],
    results: [
      { metric: "+89%", label: "RFPs sourced from organic" },
      { metric: "DR 71", label: "Domain rating after 18 months" },
    ],
    faqs: [{ q: "Does SEO move the needle for biglaw / midlaw?", a: "For practice-development partners, yes, especially for sector-specialized practices where you can dominate a niche keyword set referrals can't reach." }],
  },
];

export const getPracticeArea = (slug: string) => practiceAreas.find((p) => p.slug === slug);